import { useEffect, useRef, useState } from 'react';
import type { Usuario } from '../lib/api.ts';
import { usarTema, type Tema } from '../lib/tema.ts';

const TEMAS: Array<{ id: Tema; rotulo: string }> = [
  { id: 'claro', rotulo: 'Dia' },
  { id: 'escuro', rotulo: 'Noite' },
  { id: 'automatico', rotulo: 'Auto' },
];

/**
 * O canto direito do cabeçalho.
 *
 * Só o nome de quem entrou. Trocar senha, gerenciar acessos, tema e sair vivem
 * atrás dele — três botões soltos na barra viravam ruído numa área que a
 * pessoa olha o dia inteiro.
 *
 * **"Quem entra no Hub" só aparece para o master.** A tela esconde; o servidor
 * recusa. Esconder sem recusar não é segurança, é decoração.
 */
export function MenuUsuario({
  usuario,
  aoTrocarSenha,
  aoCriarUsuario,
  aoSair,
  variante = 'topo',
}: {
  usuario: Usuario;
  aoTrocarSenha: () => void;
  aoCriarUsuario: () => void;
  aoSair: () => void;
  /** ⭐ `lateral`: no pé do menu, com a inicial e o papel (14/09/2026). */
  variante?: 'topo' | 'lateral';
}) {
  const [aberto, setAberto] = useState(false);
  const [tema, setTema] = usarTema();
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    window.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  return (
    <div className="menu-usuario" ref={caixa}>
      <button
        type="button"
        className="menu-gatilho"
        onClick={() => setAberto(!aberto)}
        aria-expanded={aberto}
        aria-haspopup="menu"
      >
        {variante === 'lateral' ? (
          <>
            <span className="conta-avatar" aria-hidden="true">
              {usuario.nome.trim().charAt(0).toUpperCase()}
            </span>
            <span className="conta-nome">
              {usuario.nome}
              <small>{usuario.papel === 'master' ? 'master' : 'colaborador'}</small>
            </span>
          </>
        ) : (
          usuario.nome
        )}
        <span className="menu-seta" aria-hidden="true">
          {aberto ? '▴' : '▾'}
        </span>
      </button>

      {aberto && (
        <div className="menu-caixa" role="menu">
          <div className="menu-cabeca">
            <p className="menu-nome">{usuario.nome}</p>
            <p className="menu-email">{usuario.email}</p>
          </div>

          <button
            type="button"
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setAberto(false);
              aoTrocarSenha();
            }}
          >
            Trocar senha
          </button>

          {usuario.papel === 'master' && (
            <button
              type="button"
              className="menu-item"
              role="menuitem"
              onClick={() => {
                setAberto(false);
                aoCriarUsuario();
              }}
            >
              Quem entra no Hub
            </button>
          )}

          <div className="menu-tema">
            <span className="rotulo">Aparência</span>
            <div className="interruptor" role="radiogroup" aria-label="Aparência">
              {TEMAS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={tema === t.id}
                  className={'interruptor-item' + (tema === t.id ? ' interruptor-ativo' : '')}
                  onClick={() => setTema(t.id)}
                >
                  {t.rotulo}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="menu-item menu-sair"
            role="menuitem"
            onClick={() => {
              setAberto(false);
              aoSair();
            }}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  );
}
